import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiFetch } from "../../api/client";
import { TopBar } from "../../app/TopBar";
import { PageFooter } from "../../app/PageFooter";
import { useAppUser } from "../../app/AppUserContext";
import { Btn } from "../../components/ui/Button";
import { useStickyHeaderHeight } from "../../components/useStickyHeaderHeight";
import { groupByResinFamily } from "../nomenclature/database/resinGroups";
import type { ApprovedCodeView, DesignationView, GradeLinkView, ResinFamily } from "../nomenclature/types";
import { currentMonth, errorMessage, monthName } from "./format";
import { LogIntelligence } from "./LogIntelligence";
import { MakeTheCall } from "./MakeTheCall";
import { MarketBrief } from "./MarketBrief";
import { MarketIntelligence } from "./MarketIntelligence";
import { PricesInContext } from "./PricesInContext";
import { SectionMenu } from "./SectionMenu";
import { SourceDrawer } from "./SourceDrawer";
import type { SourceDrawerContent } from "./sourceDrawerContent";
import type { LogRequest, NumberedLogRequest, RegistrySlot, ResinContext } from "./types";
import { WhoHasAnnounced } from "./WhoHasAnnounced";
import { PATHS } from "../../app/paths";

interface ResinCodeData {
  resinFamilies: ResinFamily[];
  designations: DesignationView[];
  gradeLinks: GradeLinkView[];
  approvedCodes: ApprovedCodeView[];
}

const REGISTRY_SLOTS: RegistrySlot[] = ["Base", "Additive", "Color"];

const SECTION_ANCHOR_CLASSES =
  "scroll-mt-[calc(var(--cockpit-header-h,112px)+20px)] max-[1000px]:scroll-mt-[calc(var(--cockpit-header-h,112px)+72px)]";

function registryCodesFor(
  approvedCodes: ApprovedCodeView[],
  resinFamilyCode: string,
): Record<RegistrySlot, ApprovedCodeView[]> {
  const fitsResin = (approvedCode: ApprovedCodeView) =>
    !approvedCode.resinFamilyCode || approvedCode.resinFamilyCode === resinFamilyCode;
  return Object.fromEntries(
    REGISTRY_SLOTS.map((slot) => [
      slot,
      approvedCodes
        .filter((approvedCode) => approvedCode.slotName === slot && fitsResin(approvedCode))
        .sort((first, second) => first.code.localeCompare(second.code)),
    ]),
  ) as Record<RegistrySlot, ApprovedCodeView[]>;
}

export function ConsultantCockpit() {
  const navigate = useNavigate();
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [searchParams, setSearchParams] = useSearchParams();
  const { appRole, appUserId } = useAppUser();
  const [month, setMonth] = useState(currentMonth);
  const [logIntelligenceVersion, setLogIntelligenceVersion] = useState(0);
  const [resinCodeData, setResinCodeData] = useState<ResinCodeData | null>(null);
  const [resinLoadError, setResinLoadError] = useState<string | null>(null);
  const canWrite = appRole === "Uploader" || appRole === "Admin";

  const handleLogChanged = useCallback(() => setLogIntelligenceVersion((version) => version + 1), []);
  const [callVersion, setCallVersion] = useState(0);
  const handleCallsChanged = useCallback(() => setCallVersion((version) => version + 1), []);

  const headerRef = useRef<HTMLDivElement>(null);
  useStickyHeaderHeight(headerRef, "--cockpit-header-h");

  const [drawerContent, setDrawerContent] = useState<SourceDrawerContent | null>(null);
  const [logRequest, setLogRequest] = useState<NumberedLogRequest | null>(null);
  const closeDrawer = useCallback(() => setDrawerContent(null), []);
  const openInLog = (request: LogRequest) => {
    setDrawerContent(null);
    setLogRequest((previousRequest) => ({ ...request, requestNumber: (previousRequest?.requestNumber ?? 0) + 1 }));
    document.getElementById("log-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    let isStale = false;
    Promise.all([
      apiFetchRef.current("reference/resin-taxonomy").then((response) => response.json()),
      apiFetchRef.current("nomenclature/designations").then((response) => response.json()),
      apiFetchRef.current("nomenclature/grades").then((response) => response.json()),
      apiFetchRef.current("nomenclature/codes").then((response) => response.json()),
    ])
      .then(([resinFamilies, designations, gradeLinks, approvedCodes]) => {
        if (isStale) return;
        setResinCodeData({ resinFamilies, designations, gradeLinks, approvedCodes });
        setResinLoadError(null);
      })
      .catch((error) => {
        if (!isStale) setResinLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, []);

  const resinGroups = useMemo(() => {
    if (!resinCodeData) return [];
    const { resinFamilies, designations, gradeLinks } = resinCodeData;
    const activeResinFamilyCodes = new Set(resinFamilies.map((family) => family.resinFamilyCode));
    return groupByResinFamily(designations, gradeLinks, resinFamilies).filter((group) =>
      activeResinFamilyCodes.has(group.resinFamilyCode),
    );
  }, [resinCodeData]);

  const selectedResinGroup =
    resinGroups.find((group) => group.resinFamilyCode === searchParams.get("resin")) ?? resinGroups[0];
  const resin: ResinContext | null = useMemo(
    () =>
      selectedResinGroup && resinCodeData
        ? {
            resinFamilyCode: selectedResinGroup.resinFamilyCode,
            resinCodes: selectedResinGroup,
            registryCodes: registryCodesFor(resinCodeData.approvedCodes, selectedResinGroup.resinFamilyCode),
          }
        : null,
    [selectedResinGroup, resinCodeData],
  );

  const selectResin = (resinFamilyCode: string) =>
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.set("resin", resinFamilyCode);
        return nextParams;
      },
      { replace: true },
    );

  return (
    <>
      <div ref={headerRef} className="sticky top-0 z-20 bg-rs-black">
        <TopBar title="Consultant Cockpit">
          <label className="flex items-center gap-2 text-[13px] text-rs-light-gray">
            Month
            <input
              type="month"
              className="ctl w-auto"
              value={month}
              onChange={(e) => e.target.value && setMonth(e.target.value)}
            />
          </label>
          <Btn variant="ghost" onClick={() => navigate(PATHS.nomenclature)}>
            <span className="material-symbols-sharp">label</span>Resin Nomenclature
          </Btn>
          <Btn variant="ghost" onClick={() => navigate(PATHS.intake)}>
            <span className="material-symbols-sharp">upload_file</span>Data Intake Tool
          </Btn>
          {appRole === "Admin" && (
            <Btn variant="ghost" onClick={() => navigate(PATHS.adminUsers)}>
              <span className="material-symbols-sharp">admin_panel_settings</span>Users
            </Btn>
          )}
        </TopBar>

        {resinGroups.length > 0 && (
          <nav
            className="flex gap-1 px-7 bg-rs-black border-b border-rs-jet overflow-x-auto"
            role="tablist"
            aria-label="Resin family"
          >
            {resinGroups.map((group) => {
              const isSelected = group.resinFamilyCode === selectedResinGroup?.resinFamilyCode;
              return (
                <button
                  key={group.resinFamilyCode}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  className={`group flex items-baseline gap-2 whitespace-nowrap bg-transparent border-0 border-b-2 px-3.5 pt-3 pb-2.5 text-sm cursor-pointer ${
                    isSelected ? "border-rs-green" : "border-transparent"
                  }`}
                  onClick={() => selectResin(group.resinFamilyCode)}
                >
                  <b
                    className={`font-semibold group-hover:text-rs-green ${
                      isSelected ? "text-rs-green" : "text-rs-off-white"
                    }`}
                  >
                    {group.resinFamilyCode}
                  </b>
                  <small className="text-xs text-rs-light-gray">{group.displayName}</small>
                </button>
              );
            })}
          </nav>
        )}
      </div>

      {resinLoadError || !resinCodeData || !resin ? (
        <main className="pt-[26px] px-7 pb-[70px]">
          {resinLoadError ? (
            <div className="empty">
              <span className="material-symbols-sharp">error</span>Couldn't load the resin families: {resinLoadError}
            </div>
          ) : !resinCodeData ? (
            <div className="empty">
              <span className="material-symbols-sharp spin">progress_activity</span>Loading resin families…
            </div>
          ) : (
            <div className="empty">
              <span className="material-symbols-sharp">info</span>No resin families are set up yet.
            </div>
          )}
        </main>
      ) : (
        <div className="bg-rs-black">
          <div className="grid grid-cols-[220px_minmax(0,1fr)] gap-11 max-w-[1480px] mx-auto px-7 pt-7 pb-24 max-[1000px]:grid-cols-1 max-[1000px]:gap-6 max-[1000px]:pt-0 max-[700px]:px-4">
            <SectionMenu
              resinLabel={`${resin.resinFamilyCode} · ${selectedResinGroup?.displayName ?? ""}`}
              monthLabel={monthName(month)}
            />
            <main className="flex flex-col gap-16 min-w-0 p-0">
              <div id="brief-section" className={SECTION_ANCHOR_CLASSES}>
                <MarketBrief month={month} resinFamilyCode={resin.resinFamilyCode} canWrite={canWrite} />
              </div>
              <div id="nominations-section" className={SECTION_ANCHOR_CLASSES}>
                <WhoHasAnnounced
                  month={month}
                  resinFamilyCode={resin.resinFamilyCode}
                  logIntelligenceVersion={logIntelligenceVersion}
                  callVersion={callVersion}
                  appUserId={appUserId}
                  onOpenDrawer={setDrawerContent}
                />
              </div>
              <div id="prices-section" className={SECTION_ANCHOR_CLASSES}>
                <PricesInContext
                  month={month}
                  resinFamilyCode={resin.resinFamilyCode}
                  logIntelligenceVersion={logIntelligenceVersion}
                  callVersion={callVersion}
                  onOpenDrawer={setDrawerContent}
                />
              </div>
              <div id="intelligence-section" className={SECTION_ANCHOR_CLASSES}>
                <MarketIntelligence
                  month={month}
                  resinFamilyCode={resin.resinFamilyCode}
                  logIntelligenceVersion={logIntelligenceVersion}
                  onOpenDrawer={setDrawerContent}
                />
              </div>
              <div id="log-section" className={SECTION_ANCHOR_CLASSES}>
                <LogIntelligence
                  month={month}
                  resin={resin}
                  canWrite={canWrite}
                  onLogChanged={handleLogChanged}
                  logRequest={logRequest}
                />
              </div>
              <div id="call-section" className={SECTION_ANCHOR_CLASSES}>
                <MakeTheCall
                  month={month}
                  resin={resin}
                  canWrite={canWrite}
                  isAdmin={appRole === "Admin"}
                  appUserId={appUserId}
                  logIntelligenceVersion={logIntelligenceVersion}
                  onOpenMonth={setMonth}
                  onCallsChanged={handleCallsChanged}
                />
              </div>
            </main>
          </div>
        </div>
      )}

      {drawerContent && <SourceDrawer content={drawerContent} onClose={closeDrawer} onOpenInLog={openInLog} />}

      <PageFooter />
    </>
  );
}
